import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input, OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MigrationDetailsService, MigrationStatusType } from 'src/app/migration-details/migration-details.service';
import { MigrationResult } from 'src/app/migration-result/migration-result';
import { MigrationService } from 'src/app/migration-service/migration.service';
import { BentoAlertItemOptions } from '@bento/bento-ng';
import { getCompanyId } from '@onviobr/services/session';
import { HttpHeaders } from '@angular/common/http';
import { MigrationReusableService } from '../migration-reusable.service';
import { DateTimeService } from '../../migration-date-time/date-time.service';
import { TableModel } from '../table/table-model';
import { ConflictStateService } from '../../migration-conflicts/conflict-state.service';
import { ConflictResolutionCheck } from '../../migration-conflicts/migration-conflicts.component';
import { MigrationSource } from '../migration-source.enum';
import { MigrationStatus } from '../migration-status.enum';

const { IN_PROGRESS } = MigrationStatusType;

@Component({
	selector: 'app-inprogress',
	templateUrl: './inprogress.component.html',
	styleUrls: ['./inprogress.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class InprogressComponent implements OnInit, OnDestroy {
	readonly MigrationStatus = MigrationStatus;
	private readonly AUTO_REFRESH_INTERVAL = 5000; // in ms
	readonly TAX_COMPETITOR_MIGRATION_SOURCE = MigrationSource.TAXCOMPETITOR;
	alerts: BentoAlertItemOptions[] = [];

	migrationHeaderColumns = [];
	showBusyloader: boolean;
	migrationResults: Array<MigrationResult>;
	showCompletedBusyloader: boolean;
	companyId: string;
	migrationResultsUrl = '/api/migration-orchestrator/v1/results?companyId';
	migrationStatus = 'migrationStatus=INPROGRESS&migrationStatus=INQUEUE&migrationStatus=PENDINGCONFLICTS';
	shouldRefreshCompletedMigrations = false;
	lastUpdatedTimestamp: string = null;
	conflictMigration: MigrationResult;
	tableModel = new TableModel<MigrationResult>();

	@ViewChild('applicationColumnCell', { static: true }) applicationColumnCell: TemplateRef<any>;
	@ViewChild('startedOnColumnCell', { static: true }) startedOnColumnCell: TemplateRef<any>;
	@ViewChild('statusColumnCell', { static: true }) statusColumnCell: TemplateRef<any>;
	@ViewChild('progressColumnCell', { static: true }) progressColumnCell: TemplateRef<any>;

	public getCompanyId = getCompanyId;
	private autoRefreshIntervalId: number;

	/**
	 * Value that's going be used to sent as HTTP param to migration-orchestrator to filter results.
	 */
	@Input() migrationSource: MigrationSource;

	/**
	 *  Product name to display on UI e.g NetClient CS, UltraTax CS...
	 *  Expected to be used only to display product name in conflict resolution UI.
	 */
	@Input() productName: string;

	/**
	 * For Migration Dashboard the application name should show up,
	 * so this is set to true by Migration Dashboard.
	 * By default application name won't be shown
	 */
	@Input() showApplicationName = false;

	/**
	 * Migration Dashboard shows In progress inside card, so setting
	 * this flag to true will use only In progress table
	 * By default showOnlyTable will be false
	 */
	@Input() showOnlyTable = false;

	/**
	 * NetClient CS needs custom template to choose the portals, this
	 * template will be set by NetclientMigrationComponent
	 */
	@Input() customTemplate: TemplateRef <any>;

	@Output() updateMigrationResult: EventEmitter<MigrationResult[]> = new EventEmitter<MigrationResult[]>();
	@Output() gotoProduct: EventEmitter<MigrationResult> = new EventEmitter<MigrationResult>();
	@Output() lastUpdatedTimestampUpdated: EventEmitter<string> = new EventEmitter<string>();

	constructor(
		private translateService: TranslateService,
		private migrationDetails: MigrationDetailsService,
		private changeDetectorRef: ChangeDetectorRef,
		private migrationService: MigrationService,
		public reusableService: MigrationReusableService,
		private dateTimeService: DateTimeService,
		private conflictStateService: ConflictStateService) {}

	async ngOnInit() {
		if (this.migrationSource === MigrationSource.TAXCOMPETITOR && this.productName) {
			throw new Error('productName should be initially null if migrationSource is TAXCOMPETITOR');
		}
		this.companyId = await this.getCompanyId();
		const inProgressList = this.migrationDetails.getCachedMigrationResults(this.migrationSource, IN_PROGRESS);
		if (inProgressList?.length) {
			this.shouldRefreshCompletedMigrations = true;
		}

		await this.setUpTable();
		await this.getInProgressMigrationStatus();
	}

	private async setUpTable() {
		const tableModel = new TableModel<MigrationResult>();

		if (this.showApplicationName) {
			const translationKey = 'onvio.setup.migrations.status.inprogress.col.application';
			tableModel.addColumn({
				columnHeaderText: await this.translate(translationKey),
				cellTemplate: this.applicationColumnCell
			});
		}

		tableModel.addColumn({
			columnHeaderText: await this.translate('onvio.setup.migrations.status.inprogress.col.startedOn'),
			cellTemplate: this.startedOnColumnCell
		})
			.addColumn({
				columnHeaderText: await this.translate('onvio.setup.migrations.status.inprogress.col.status'),
				cellTemplate: this.statusColumnCell
			})
			.addColumn({
				columnHeaderText: await this.translate('onvio.setup.migrations.status.inprogress.col.progress'),
				cellTemplate: this.progressColumnCell
			});
		this.tableModel = tableModel;
	}

	private translate(translationKey: string): Promise<string> {
		return this.translateService.get(translationKey).toPromise();
	}

	async updateInProgressMigrationResult(showBusyLoader = true): Promise<void> {
		this.shouldRefreshCompletedMigrations = true;
		await this.getInProgressMigrationStatus(showBusyLoader);
	}

	async getInProgressMigrationStatus(showBusyLoader = true) {
		this.updateLastUpdatedTimestamp();
		const headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});
		this.showBusyloader = showBusyLoader;
		this.changeDetectorRef.markForCheck();

		try {
			const migrationResults = await this.migrationService
				.get(`${this.migrationResultsUrl}=${this.companyId}&${this.migrationStatus}${this.getMigrationSourceParam()}`, headers)
				.toPromise<[MigrationResult]>();

			if (migrationResults?.length) {
				this.migrationResults = migrationResults;
				// only 1 NEW/INPROGRESS item can be present
				if (migrationResults.length > 1) {
					console.warn('More than 1 New/Inprogress migration item present', migrationResults);
				}
				this.setAutoRefreshInterval();
			} else {
				this.migrationResults = null;
				// all inprogress migrations become completed so it's needed to refresh list of completed migrations
				if (this.shouldRefreshCompletedMigrations) {
					this.reusableService.refreshCompletedResults();
				}
				this.shouldRefreshCompletedMigrations = false;
				this.clearAutoRefreshInterval();
			}
			this.tableModel.setItems(this.migrationResults);
			this.migrationDetails.cacheMigrationResults(this.migrationSource, IN_PROGRESS, this.migrationResults);
			this.showBusyloader = false;
			this.updateMigrationResult.emit(this.migrationResults);

			this.updateConflictMigration();

			this.changeDetectorRef.markForCheck();
		} catch (err) {
			const errorMessage = this.translateService.instant(
				'onvio.setup.migrations.netfirm.http.error',
				{ action: 'In Progress Migration Results', error: err }
			);
			this.showError(errorMessage);
			this.showBusyloader = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	private getMigrationSourceParam(): string {
		// migration source is applicable only for products; for dashboard, fetch all migration result
		if (this.migrationSource && this.migrationSource !== MigrationSource.ALL) {
			return `&migrationSource=${this.migrationSource}`;
		} else {
			return '';
		}
	}

	private updateConflictMigration(): void {
		if (this.isActiveTabMigrationDashboard()) {
			// Conflict Resolution UI should not be displayed on migration dashboard tab;
			return;
		}

		this.conflictMigration = this.getConflictMigration();
		if (this.conflictMigration) {
			if (this.migrationSource === MigrationSource.TAXCOMPETITOR) {
				this.productName = this.conflictMigration.metadata.competitorDescription;
			}
		} else {
			this.conflictStateService.setLastConflictResolved(true);
		}
	}

	private getConflictMigration(): MigrationResult {
		if (!this.migrationResults) {
			return null;
		}
		const conflictMigrations = this.migrationResults.filter(m => m.migrationStatus === MigrationStatus.PENDING_CONFLICTS);
		if (conflictMigrations.length === 0) {
			return null;
		}
		if (conflictMigrations.length > 1) {
			console.warn('More than one migration in conflict. This may be an issue and result in undefined behavior');
		}

		return conflictMigrations[0];
	}

	private isActiveTabMigrationDashboard(): boolean {
		return this.migrationSource === MigrationSource.ALL;
	}

	handleAllConflictsResolved($event: ConflictResolutionCheck): void {
		if ($event === ConflictResolutionCheck.AFTER_SUBMIT_RESOLVED_BUTTON_CLICKED) {
			const message = this.translateService.instant('onvio.setup.migrations.conflict.yourMigrationWillContinue');
			this.showSuccessMessage(message);
		}
		this.hideConflictResolutionUI();
	}

	private hideConflictResolutionUI(): void {
		this.conflictMigration = null;
	}

	private showSuccessMessage(message: string): void {
		this.alerts.push({
			type: 'success',
			msg: message,
			closeable: true
		});
	}

	private showError(message: string, type: string = 'danger') {
		this.alerts.push({
			type,
			msg: message,
			closeable: true
		});
	}

	private updateLastUpdatedTimestamp() {
		const formattedDateTime = this.dateTimeService.getDateTimeFromJSDate(new Date());
		this.lastUpdatedTimestamp = formattedDateTime;
		this.lastUpdatedTimestampUpdated.emit(formattedDateTime);
		this.changeDetectorRef.markForCheck();
	}

	ngOnDestroy(): void {
		this.clearAutoRefreshInterval();
	}

	private setAutoRefreshInterval(): void {
		if (!this.autoRefreshIntervalId) {
			this.autoRefreshIntervalId = window.setInterval(() => {
				const showBusyLoader = false;
				this.updateInProgressMigrationResult(showBusyLoader);
			}, this.AUTO_REFRESH_INTERVAL);
		}
	}

	clearAutoRefreshInterval(): void {
		if (this.autoRefreshIntervalId) {
			clearInterval(this.autoRefreshIntervalId);
			this.autoRefreshIntervalId = null;
		}
	}
}
